import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Upload, RefreshCw, Activity, AlertCircle, CheckCircle2, XCircle, Webhook,
} from "lucide-react";

/**
 * Admin: catch up xpay_links statuses when webhooks weren't arriving.
 *
 * Three tools on one page:
 *   1. CSV upload (InstaXchange activity export)   → bulk reconcile
 *   2. "Poll stale" button                         → walk the API
 *   3. Webhook health diagnostics card             → is upstream calling us?
 */
export default function AdminXpayReconcile() {
  const [health, setHealth] = useState(null);
  const [file, setFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [pollBusy, setPollBusy] = useState(false);
  const [pollResult, setPollResult] = useState(null);

  const loadHealth = async () => {
    try {
      const r = await api.get("/admin/xpay/webhook-health");
      setHealth(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load webhook health");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadHealth(); }, []);

  const uploadCsv = async () => {
    if (!file) { toast.error("Choose a CSV first"); return; }
    setCsvBusy(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/xpay/reconcile-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(r.data);
      toast.success(`Read ${r.data.rows_read} rows · updated ${r.data.updated}`);
      loadHealth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV reconcile failed");
    } finally { setCsvBusy(false); }
  };

  const pollStale = async () => {
    setPollBusy(true);
    setPollResult(null);
    try {
      const r = await api.post("/admin/xpay/reconcile-stale?older_than_min=5&limit=250");
      setPollResult(r.data);
      toast.success(`Scanned ${r.data.scanned} · updated ${r.data.updated}`);
      loadHealth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stale-poll failed");
    } finally { setPollBusy(false); }
  };

  const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <AdminLayout
      title="XPay reconciliation"
      subtitle="Catch up payment-link statuses when webhooks didn't arrive — bulk CSV import, on-demand API poll, and live webhook health."
    >
      {/* Webhook health */}
      <div className="xch-card p-5 mb-6" data-testid="xpay-reconcile-health">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Webhook className="w-4 h-4 text-emerald-600" /> Webhook health
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Is InstaXchange actually calling our endpoint? If you&apos;ve never received one, fix the dashboard URL.
            </p>
          </div>
          <Button onClick={loadHealth} variant="outline" size="sm" className="rounded-xl gap-1.5"
                  data-testid="xpay-reconcile-health-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
        {!health && <div className="text-xs text-muted-foreground">Loading…</div>}
        {health && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            <Stat label="Webhook secret in env" ok={health.webhook_secret_configured}
                  data-testid="xpay-health-secret" />
            <Stat label="InstaXchange API wired" ok={health.instaxchange_api_wired}
                  data-testid="xpay-health-api" />
            <Stat label="Have we ever received one?" ok={(health.links_with_webhook_ever || 0) > 0}
                  detail={`${health.links_with_webhook_ever} of ${health.links_total} links`}
                  data-testid="xpay-health-ever" />
            <div className="rounded-xl border border-border p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Last webhook</div>
              <div className="text-sm mt-1">{fmt(health.last_webhook_at)}</div>
              {health.last_webhook_session_id && (
                <code className="text-[10px] font-mono text-muted-foreground block truncate">{health.last_webhook_session_id}</code>
              )}
            </div>
            <div className="rounded-xl border border-border p-3 col-span-full">
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Status breakdown</div>
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(health.status_breakdown || {}).map(([k, n]) => (
                  <Badge key={k} variant="outline" className="font-mono"
                          data-testid={`xpay-health-status-${k}`}>
                    {k}: {n}
                  </Badge>
                ))}
              </div>
              {(health.links_stuck_initiated || 0) > 0 && (
                <div className="text-xs text-amber-700 mt-2 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {health.links_stuck_initiated} link{health.links_stuck_initiated === 1 ? "" : "s"} stuck at &ldquo;initiated&rdquo; — run CSV import or stale-poll below.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* CSV import */}
      <div className="xch-card p-5 mb-6" data-testid="xpay-reconcile-csv">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <Upload className="w-4 h-4 text-indigo-600" /> Bulk import from InstaXchange CSV
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">
          Export your &ldquo;Activity Report&rdquo; from the InstaXchange back office (Transactions → Export) and drop it here.
          We match by <code className="bg-muted px-1 rounded">session_id</code> and mirror the upstream <code className="bg-muted px-1 rounded">status</code>.
          Idempotent — re-uploading the same file is safe.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept=".csv,text/csv"
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setCsvResult(null); }}
                  className="text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-emerald-50 file:text-emerald-700 file:font-semibold hover:file:bg-emerald-100"
                  data-testid="xpay-reconcile-csv-input" />
          <Button onClick={uploadCsv} disabled={!file || csvBusy} size="sm" className="rounded-xl gap-1.5"
                  data-testid="xpay-reconcile-csv-submit">
            <Upload className="w-3.5 h-3.5" /> {csvBusy ? "Importing…" : "Import"}
          </Button>
        </div>
        {csvResult && (
          <div className="mt-4 grid sm:grid-cols-4 gap-2 text-xs" data-testid="xpay-reconcile-csv-result">
            <Pill label="Read"     n={csvResult.rows_read}       tone="neutral" />
            <Pill label="Updated"  n={csvResult.updated}         tone="ok" />
            <Pill label="Same"     n={csvResult.skipped_same}    tone="neutral" />
            <Pill label="Unknown" n={csvResult.unknown_session} tone={csvResult.unknown_session > 0 ? "warn" : "neutral"} />
            <Pill label="Errors"  n={csvResult.errors}          tone={csvResult.errors > 0 ? "bad" : "neutral"} />
            {(csvResult.updated_detail || []).slice(0, 8).length > 0 && (
              <div className="col-span-full mt-2">
                <details>
                  <summary className="text-xs text-muted-foreground cursor-pointer">First {Math.min(8, csvResult.updated_detail.length)} of {csvResult.updated} updates</summary>
                  <ul className="text-[11px] font-mono mt-2 space-y-1">
                    {csvResult.updated_detail.slice(0, 8).map((u, i) => (
                      <li key={i} className="text-muted-foreground">
                        {u.session_id?.slice(0, 16)}… {u.prev} → <span className="text-emerald-700">{u.new}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stale poll */}
      <div className="xch-card p-5" data-testid="xpay-reconcile-poll">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <Activity className="w-4 h-4 text-rose-600" /> Re-poll stale links via API
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">
          For every link still in a non-terminal state (older than 5 minutes), call <code className="bg-muted px-1 rounded">GET /api/session/&#123;id&#125;/status</code> at InstaXchange and mirror.
          Slower than the CSV (one round-trip per row), but doesn&apos;t need an export.
        </p>
        <Button onClick={pollStale} disabled={pollBusy} size="sm" className="rounded-xl gap-1.5"
                data-testid="xpay-reconcile-poll-submit">
          <Activity className="w-3.5 h-3.5" /> {pollBusy ? "Polling…" : "Run now"}
        </Button>
        {pollResult && (
          <div className="mt-4 grid sm:grid-cols-4 gap-2 text-xs" data-testid="xpay-reconcile-poll-result">
            <Pill label="Scanned" n={pollResult.scanned}  tone="neutral" />
            <Pill label="Updated" n={pollResult.updated}  tone="ok" />
            <Pill label="No change" n={pollResult.no_change} tone="neutral" />
            <Pill label="Errors"  n={pollResult.errors}   tone={pollResult.errors > 0 ? "bad" : "neutral"} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Stat({ label, ok, detail, "data-testid": dti }) {
  return (
    <div className="rounded-xl border border-border p-3" data-testid={dti}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className="flex items-center gap-2 mt-1">
        {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
             : <XCircle className="w-4 h-4 text-rose-600" />}
        <span className={`text-sm font-semibold ${ok ? "text-emerald-700" : "text-rose-700"}`}>
          {ok ? "OK" : "Not configured"}
        </span>
      </div>
      {detail && <div className="text-[11px] text-muted-foreground mt-0.5">{detail}</div>}
    </div>
  );
}

function Pill({ label, n, tone }) {
  const styles = {
    ok:      "bg-emerald-50 border-emerald-200 text-emerald-700",
    bad:     "bg-rose-50 border-rose-200 text-rose-700",
    warn:    "bg-amber-50 border-amber-200 text-amber-700",
    neutral: "bg-muted/40 border-border text-foreground",
  };
  return (
    <div className={`rounded-xl border p-2.5 ${styles[tone] || styles.neutral}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums">{n}</div>
    </div>
  );
}
